import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownButton,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faDownload, faPrint } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { pdf } from '@react-pdf/renderer';
import moment from 'moment';
import * as pdfjs from 'pdfjs-dist';
import {
  PDFDocumentProxy,
  RenderParameters,
} from 'pdfjs-dist/types/src/display/api';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import { getLocaleCode, Language } from 'src/models/Language.model';
import { Product } from 'src/models/Product.model';
import { ReportTemplate } from 'src/models/ReportTemplate.model';
import { ReportType, ReportTypeName } from 'src/models/ReportType.model';
import { Session } from 'src/models/Session.model';

import useUiTranslation from 'src/hooks/useUiTranslation';

import { captureUserActionError } from 'src/utils/errorReporting';

import 'pdfjs-dist/build/pdf.mjs';
import 'pdfjs-dist/web/pdf_viewer.css';
import ReportRenderer from './ReportRenderer';

// Copy file from node_modules/pdfjs-dist/legacy/build/pdf.worker.min.mjs to public/js folder
pdfjs.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.min.mjs';

if (Promise.withResolvers === undefined) {
  if (window) {
    // @ts-expect-error This does not exist outside of polyfill which this is doing
    window.Promise.withResolvers = function () {
      let resolve, reject;
      const promise = new Promise((_resolve, _reject) => {
        resolve = _resolve;
        reject = _reject;
      });
      return { promise, resolve, reject };
    };
  } else {
    // @ts-expect-error This does not exist outside of polyfill which this is doing
    global.Promise.withResolvers = function () {
      let resolve, reject;
      const promise = new Promise((_resolve, _reject) => {
        resolve = _resolve;
        reject = _reject;
      });
      return { promise, resolve, reject };
    };
  }
}

interface IReportViewerProps {
  show: boolean;
  session: Session;
  handleCloseReportViewer: () => void;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('loadend', () => {
      // Resolve with the Base64 string (removing the prefix)
      const base64data = reader.result as string;
      resolve(base64data.split(',')[1]); // Returns the Base64 part only
    });

    reader.addEventListener('error', () => {
      reject(new Error('Failed to convert blob to Base64'));
    });

    reader.readAsDataURL(blob); // Read the Blob as a data URL
  });
}

const downloadFile = async (url: string, filename: string, blob: Blob) => {
  // Use client api?
  if (window._cdvElectronIpc?.saveReportFile) {
    const encoded = await blobToBase64(blob);
    window._cdvElectronIpc.saveReportFile(filename, encoded);
    return;
  }

  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

const usePreparePdf = (
  session: Session,
  type: ReportType,
  logo: string,
  language: string,
  historyIndex: number | undefined,
) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [blobData, setBlobData] = useState<Blob>();
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy>();
  const [numPages, setNumPages] = useState(0);

  // Prepare pdf: generate and load pdf blob
  useEffect(() => {
    if (session !== undefined) {
      const preparePdf = async () => {
        const blob = await pdf(
          <ReportRenderer
            show={true}
            session={session}
            historyIndex={historyIndex}
            type={type}
            logo={logo}
          />,
        ).toBlob();
        setBlobData(blob);

        // Create a Blob URL from the PDF Blob
        const blobUrl = URL.createObjectURL(blob);
        setPdfUrl(blobUrl);

        pdfjs.getDocument(blobUrl).promise.then((doc) => {
          setPdfDoc(doc);
          setNumPages(doc.numPages);
        });
      };

      preparePdf();
    }
  }, [session, type, logo, language, historyIndex]);

  return { pdfUrl, blobData, pdfDoc, numPages };
};

const PdfPage = ({
  pdfDoc,
  pageNumber,
  language,
}: {
  pdfDoc: PDFDocumentProxy;
  pageNumber: number;
  language: string;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    let renderTask: pdfjs.RenderTask | null = null;

    const renderPage = async () => {
      const page = await pdfDoc.getPage(pageNumber);

      const canvas = canvasRef.current;
      if (!canvas) return;

      let viewport = page.getViewport({ scale: 1 });
      const scale = canvas.width / viewport.width;
      viewport = page.getViewport({ scale });

      const context = canvas.getContext('2d');
      if (!context) return;

      const renderContext: RenderParameters = {
        canvasContext: context,
        viewport,
      };

      renderTask = page.render(renderContext);
      try {
        await renderTask.promise;
      } catch (error) {
        const err = error as Error;
        if (err.name === 'RenderingCancelledException') {
          // Ignore
          return;
        }
        throw err;
      }
    };

    renderPage();

    return () => {
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [pdfDoc, pageNumber, language]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="react-pdf__Page__canvas"
        width="1080"
        height="1527"
      ></canvas>
    </>
  );
};

// Check if the pdf viewer is enabled in the browser
// Will use the pdf viewer if it is enabled, otherwise will use pdf.js as fallback
function isPdfViewerEnabled(): boolean {
  return navigator.pdfViewerEnabled;
}

const ReportViewer = ({
  show,
  session,
  handleCloseReportViewer,
}: IReportViewerProps) => {
  const [type, setType] = useState(
    session.template === ReportTemplate.OPTAIN_SMALL_DR
      ? ReportType.OPTAIN
      : ReportType.CLINICAL,
  );
  const [historyIndex, setHistoryIndex] = useState(
    session.history && session.history.length > 0
      ? session.history.length - 1
      : undefined,
  );

  const { tCommon } = useUiTranslation();
  const {
    i18n: { changeLanguage, language },
  } = useTranslation();
  const {
    userSettings: { userPermission },
    practiceSettings: { product },
    logo,
  } = useRemoteBoundStore();

  const { setReportLanguage } = useLocalStore();

  const { pdfUrl, blobData, pdfDoc, numPages } = usePreparePdf(
    session,
    type,
    logo,
    language,
    historyIndex,
  );

  const handlePrintReport = async () => {
    try {
      const allowed = userPermission.print;

      if (allowed !== true) {
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: tCommon('message.error.reportPrintPermissionError'),
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
        return;
      }

      // use client api?
      if (window._cdvElectronIpc?.printReportFile) {
        // fun printReportFile(base64String: String): Boolean
        const encoded = await blobToBase64(blobData!);
        window._cdvElectronIpc.printReportFile(encoded);
        return;
      }

      // use embedded pdf viewer?
      if (isPdfViewerEnabled()) {
        const reportFrame = document.querySelector(
          '#report-viewer-frame',
        ) as HTMLIFrameElement;
        reportFrame.contentWindow?.print();
        return;
      }

      // print pdf canvas with jQuery printThis plugin
      window.$('canvas.react-pdf__Page__canvas').printThis({
        importCSS: false,
        formValues: false,
        removeScripts: true,
        canvas: true,
        debug: true, // Keep iframe for print preview
        loadCSS: '/css/print.css', // Css file for print layout
      });
    } catch (error) {
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message:
          tCommon('message.error.reportPrintError') +
          ' ' +
          Api.formatException((error as Error).message),
        locale: getLocaleCode(language as Language),
        type: 'error',
      });

      captureUserActionError(error, 'report_print', {
        customMessage: 'Error printing report',
        tags: {
          reportType: type,
        },
      });
    }
  };

  const handleDownloadReport = async () => {
    if (session.iid === undefined) {
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message: tCommon(`message.error.reportDownloadSessionError`),
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
      return;
    }

    try {
      const allowed = userPermission.print;

      if (allowed !== true) {
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: tCommon(`message.error.reportDownloadPermissionError`),
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
        return;
      }

      const title =
        tCommon(`report.${ReportTypeName(type)}`) +
        ' ' +
        session.id +
        ' ' +
        moment().format('YYYY-MM-DD');

      downloadFile(pdfUrl, title + '.pdf', blobData!);
    } catch (error) {
      Api.alertBox({
        title: tCommon('bootbox.title.Error'),
        message:
          tCommon('message.error.reportDownloadError') +
          ' ' +
          Api.formatException((error as Error).message),
        locale: getLocaleCode(language as Language),
        type: 'error',
      });
    }
  };

  const languageOptions =
    product === Product.ASSUREPLUS_AU
      ? [Language.ENGLISH]
      : Object.values(Language);

  return (
    <CustomModal
      show={show}
      onHide={handleCloseReportViewer}
      centered
      size="xl"
      scrollable={true}
      fullscreen="xl-down"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ height: '55px' }}>
          <span style={{ lineHeight: '55px' }}>{tCommon('report.title')}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="report-content p-0"
        style={{
          width: '100%',
          height: '100vh',
          overflowY: isPdfViewerEnabled() ? 'hidden' : 'auto',
        }}
      >
        {pdfUrl ? (
          isPdfViewerEnabled() ? (
            <iframe
              id="report-viewer-frame"
              src={`${pdfUrl}#zoom=138&toolbar=0&navpanes=0&scrollbar=0`}
              title="Report"
              style={{ width: '100%', height: '100%' }}
            />
          ) : (
            <div id="report-container">
              {Array.from({ length: numPages }, (_, index) => index + 1).map(
                (pageNumber) => (
                  <PdfPage
                    key={pageNumber}
                    pdfDoc={pdfDoc!}
                    pageNumber={pageNumber}
                    language={language}
                  />
                ),
              )}
            </div>
          )
        ) : (
          <div className="p-4 w-100 text-center">
            <Spinner className="mx-5 my-5" variant="primary" />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {session.history && session.history.length > 0 && (
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary">
              {moment(session.history[historyIndex || 0]?.SavedTime).format(
                'YYYY-MM-DD HH:mm:ss',
              )}
              {historyIndex === 0 && (
                <span> ({tCommon('reportEditor.original')})</span>
              )}
              {historyIndex === session.history.length - 1 && (
                <span> ({tCommon('reportEditor.latest')})</span>
              )}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {session.history.map((history, index) => (
                <Dropdown.Item
                  key={history.SavedTime.toString()}
                  onClick={() => {
                    setHistoryIndex(index);
                  }}
                  active={historyIndex === index}
                >
                  {moment(history.SavedTime).format('YYYY-MM-DD HH:mm:ss')}
                  {index === 0 && (
                    <span> ({tCommon('reportEditor.original')})</span>
                  )}
                  {index === session.history!.length - 1 && (
                    <span> ({tCommon('reportEditor.latest')})</span>
                  )}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {(session.template === ReportTemplate.ASSURE_LARGE_EYE ||
          session.template === ReportTemplate.ASSURE_SMALL_EYE ||
          session.template === ReportTemplate.ASSUREPLUS_LARGE_EYE_CVD ||
          session.template === ReportTemplate.ASSUREPLUS_SMALL_EYE_CVD ||
          session.template === ReportTemplate.ASSUREPLUS_LARGE_EYE_CVD_AUSCVD ||
          session.template ===
            ReportTemplate.ASSUREPLUS_SMALL_EYE_CVD_AUSCVD) && (
          <>
            <DropdownButton
              variant="outline-primary"
              data-test="report-type-dropdown"
              title={
                type === ReportType.CLINICAL
                  ? tCommon(`report.${ReportTypeName(ReportType.CLINICAL)}`) +
                    ' '
                  : tCommon(`report.${ReportTypeName(ReportType.PATIENT)}`) +
                    ' '
              }
            >
              <Dropdown.Item
                onClick={() => setType(ReportType.CLINICAL)}
                active={type === ReportType.CLINICAL}
              >
                {tCommon(`report.${ReportTypeName(ReportType.CLINICAL)}`)}
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => setType(ReportType.PATIENT)}
                active={type === ReportType.PATIENT}
              >
                {tCommon(`report.${ReportTypeName(ReportType.PATIENT)}`)}
              </Dropdown.Item>
            </DropdownButton>

            <Dropdown>
              <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
                {tCommon(`report.languageEnum.${language}`)}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                {languageOptions.map((lang) => (
                  <Dropdown.Item
                    key={lang}
                    active={language === lang}
                    onClick={async () => {
                      await changeLanguage(lang);
                      setReportLanguage(lang);
                    }}
                  >
                    {tCommon(`report.languageEnum.${lang}`)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </>
        )}

        <Button
          variant="primary"
          onClick={handleDownloadReport}
          data-test="download-btn"
          disabled={!blobData}
        >
          <FontAwesomeIcon icon={faDownload} className="me-1"></FontAwesomeIcon>
          {tCommon('report.download')}
        </Button>

        <Button
          variant="primary"
          onClick={handlePrintReport}
          disabled={!blobData}
        >
          <FontAwesomeIcon icon={faPrint} className="me-1"></FontAwesomeIcon>
          {tCommon('report.print')}
        </Button>
      </Modal.Footer>
    </CustomModal>
  );
};

export default ReportViewer;
